/**
 * Security & Compliance page - Comprehensive redesign
 */

import React from 'react';
import {Link} from 'react-router-dom';
import {LandingLayout} from '../../components/layout/LandingLayout';
import {Container} from '../../components/ui/Container';
import {Section} from '../../components/ui/Section';
import {SectionHeader} from '../../components/ui/SectionHeader';
import {Card} from '../../components/ui/Card';
import {Button} from '../../components/ui/Button';
import {SecurityFeatureGrid} from '../../components/landing/SecurityFeatureGrid';
import {ROUTES} from '../../config/constants';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

// Security features - removed emojis, using clean design
const securityFeatures = [
  {
    title: 'End-to-End Encryption',
    description: 'All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption. Your meeting data is protected with military-grade security standards.',
  },
  {
    title: 'Zero Data Retention',
    description: 'No training on your data by VoxaPilot or AI providers. Your meeting data remains private and is never used to train models. You control data retention policies.',
  },
  {
    title: 'Identity Management',
    description: 'SAML-based SSO integration for secure user access with support for all major identity providers including Okta, Azure AD, Google Workspace, and more.',
  },
  {
    title: 'Role-Based Access Control',
    description: 'Granular permissions and access controls for teams and individuals. Define who can access what data with enterprise-grade permission management.',
  },
  {
    title: 'Centralized Security Controls',
    description: 'Configure model access, privacy settings, and retention policies across your entire organization from a single dashboard with comprehensive admin controls.',
  },
  {
    title: 'Comprehensive Audit Logs',
    description: 'Complete audit logging for all actions and data access. Track every interaction, export logs for compliance, and maintain full visibility into system activity.',
  },
  {
    title: 'Data Residency Options',
    description: 'Choose where your data is stored with regional data residency options. Meet data sovereignty requirements for your organization and industry.',
  },
  {
    title: 'Regular Security Audits',
    description: 'Annual penetration testing, continuous security monitoring, and third-party security assessments ensure your data is protected against emerging threats.',
  },
  {
    title: 'Incident Response',
    description: '24/7 security monitoring with rapid incident response protocols. Our security team is always ready to address and resolve any security concerns immediately.',
  },
];

// Compliance standards
const complianceStandards = [
  {
    name: 'SOC 2 Type II',
    description: 'Security and availability controls verified through annual audits',
    status: 'Certified',
  },
  {
    name: 'GDPR',
    description: 'Full compliance with European data protection regulations',
    status: 'Compliant',
  },
  {
    name: 'CCPA',
    description: 'California Consumer Privacy Act compliance for data protection',
    status: 'Compliant',
  },
  {
    name: 'HIPAA',
    description: 'Healthcare industry compliance for protected health information',
    status: 'Ready',
  },
  {
    name: 'ISO 27001',
    description: 'International information security management system standard',
    status: 'Certified',
  },
  {
    name: 'SOC 2 Type I',
    description: 'Initial security controls assessment and verification',
    status: 'Certified',
  },
];

// Data protection details
const dataProtectionFeatures = [
  {
    title: 'Encryption at Rest',
    description: 'AES-256 encryption for all stored data with key management through industry-leading providers',
  },
  {
    title: 'Encryption in Transit',
    description: 'TLS 1.3 for all data transmission with perfect forward secrecy and strong cipher suites',
  },
  {
    title: 'Key Management',
    description: 'Hardware security modules (HSM) for key storage and rotation with automated key lifecycle management',
  },
  {
    title: 'Network Security',
    description: 'DDoS protection, WAF, and network segmentation to prevent unauthorized access and attacks',
  },
];

// Infrastructure security
const infrastructureSecurity = [
  {
    title: 'Cloud Infrastructure',
    description: 'Built on AWS and Google Cloud with redundant data centers, automated backups, and 99.9% uptime SLA',
  },
  {
    title: 'Access Controls',
    description: 'Multi-factor authentication required for all admin access, with role-based permissions and regular access reviews',
  },
  {
    title: 'Monitoring & Alerting',
    description: '24/7 security monitoring with automated threat detection and real-time alerting for suspicious activities',
  },
  {
    title: 'Disaster Recovery',
    description: 'Automated backups with point-in-time recovery, tested disaster recovery procedures, and geographic redundancy',
  },
];

export function SecurityPage() {
  const {ref: heroRef, isVisible: heroVisible} = useScrollAnimation();
  const {ref: overviewRef, isVisible: overviewVisible} = useScrollAnimation();
  const {ref: featuresRef, isVisible: featuresVisible} = useScrollAnimation();
  const {ref: complianceRef, isVisible: complianceVisible} = useScrollAnimation();
  const {ref: dataProtectionRef, isVisible: dataProtectionVisible} = useScrollAnimation();
  const {ref: infrastructureRef, isVisible: infrastructureVisible} = useScrollAnimation();
  const {ref: ctaRef, isVisible: ctaVisible} = useScrollAnimation();

  return (
    <LandingLayout>
      {/* Hero Section */}
      <Section padding="lg" background="white" className="pt-32 pb-20 md:pb-24 bg-background dark:bg-[#1E1E1E]">
        <Container>
          <div
            ref={heroRef as React.RefObject<HTMLDivElement>}
            className={`max-w-4xl mx-auto text-center transition-all duration-700 ${
              heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-textPrimary dark:text-[#CCCCCC] mb-6 leading-tight">
              Security & Compliance
            </h1>
            <p className="text-xl sm:text-2xl text-textSecondary dark:text-[#858585] max-w-3xl mx-auto leading-relaxed">
              Enterprise-grade security for your sensitive meeting data. Built with privacy and compliance at the core.
            </p>
          </div>
        </Container>
      </Section>

      {/* Security Overview */}
      <Section padding="lg" background="gray" ref={overviewRef as React.RefObject<HTMLElement>} className="bg-backgroundSecondary dark:bg-[#252526]">
        <Container>
          <div className={`max-w-4xl mx-auto transition-all duration-700 ${
            overviewVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl p-8 md:p-12 border border-gray-200 dark:border-gray-700 shadow-lg">
              <div className="prose prose-lg max-w-none text-textSecondary dark:text-[#858585] leading-relaxed space-y-4">
                <p className="text-lg">
                  At VoxaPilot, security isn't an afterthought—it's foundational. We understand that meeting data often contains sensitive information, strategic discussions, and confidential insights. That's why we've built our platform with enterprise-grade security from the ground up.
                </p>
                <p>
                  Our security architecture follows industry best practices and exceeds regulatory requirements. We maintain multiple compliance certifications, conduct regular security audits, and implement defense-in-depth strategies to protect your data at every layer.
                </p>
                <p>
                  Whether you're a startup handling customer conversations or a Fortune 500 managing board meetings, VoxaPilot provides the security controls and compliance certifications you need to meet your organization's requirements.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Security Features */}
      <Section padding="lg" background="white" ref={featuresRef as React.RefObject<HTMLElement>} className="bg-background dark:bg-[#1E1E1E]">
        <Container>
          <div className={`transition-all duration-700 ${
            featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <SectionHeader
              title="Security Features"
              subtitle="Comprehensive protection at every layer"
              centered
            />
            <SecurityFeatureGrid features={securityFeatures} />
          </div>
        </Container>
      </Section>

      {/* Compliance Standards */}
      <Section padding="lg" background="gray" ref={complianceRef as React.RefObject<HTMLElement>} className="bg-backgroundSecondary dark:bg-[#252526]">
        <Container>
          <div className={`transition-all duration-700 ${
            complianceVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <SectionHeader
              title="Compliance & Certifications"
              subtitle="Meeting regulatory requirements across industries and regions"
              centered
            />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {complianceStandards.map((standard, index) => (
                  <div
                    key={standard.name}
                    className={`transition-all duration-700 ${
                      complianceVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                    style={{transitionDelay: `${0.2 + (index * 0.1)}s`}}
                  >
                    <Card hover className="p-6 h-full">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-xl font-semibold text-textPrimary dark:text-[#CCCCCC]">
                            {standard.name}
                          </h3>
                          <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-medium">
                            {standard.status}
                          </span>
                        </div>
                        <p className="text-sm text-textSecondary dark:text-[#858585] leading-relaxed">
                          {standard.description}
                        </p>
                      </div>
                    </Card>
                  </div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Data Protection */}
      <Section padding="lg" background="white" ref={dataProtectionRef as React.RefObject<HTMLElement>} className="bg-background dark:bg-[#1E1E1E]">
        <Container>
          <div className={`transition-all duration-700 ${
            dataProtectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <SectionHeader
              title="Data Protection"
              subtitle="Multiple layers of encryption and security controls"
              centered
            />
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {dataProtectionFeatures.map((feature, index) => (
                  <div
                    key={feature.title}
                    className={`transition-all duration-700 ${
                      dataProtectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                    style={{transitionDelay: `${0.2 + (index * 0.1)}s`}}
                  >
                    <Card hover className="p-6 md:p-8">
                      <h3 className="text-lg font-semibold text-textPrimary dark:text-[#CCCCCC] mb-3">
                        {feature.title}
                      </h3>
                      <p className="text-base text-textSecondary dark:text-[#858585] leading-relaxed">
                        {feature.description}
                      </p>
                    </Card>
                  </div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Infrastructure Security */}
      <Section padding="lg" background="gray" ref={infrastructureRef as React.RefObject<HTMLElement>} className="bg-backgroundSecondary dark:bg-[#252526]">
        <Container>
          <div className={`transition-all duration-700 ${
            infrastructureVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <SectionHeader
              title="Infrastructure Security"
              subtitle="Secure, reliable, and scalable cloud infrastructure"
              centered
            />
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {infrastructureSecurity.map((feature, index) => {
                const {ref, isVisible} = useScrollAnimation();
                return (
                  <div
                    key={feature.title}
                    ref={ref as React.RefObject<HTMLDivElement>}
                    className={`transition-all duration-700 ${
                      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                    style={{transitionDelay: `${index * 0.1}s`}}
                  >
                    <Card hover className="p-6 md:p-8">
                      <h3 className="text-lg font-semibold text-textPrimary dark:text-[#CCCCCC] mb-3">
                        {feature.title}
                      </h3>
                      <p className="text-base text-textSecondary dark:text-[#858585] leading-relaxed">
                        {feature.description}
                      </p>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </Container>
      </Section>

      {/* CTA Section */}
      <Section padding="lg" background="white" ref={ctaRef as React.RefObject<HTMLElement>} className="bg-background dark:bg-[#1E1E1E]">
        <Container>
          <div className={`max-w-3xl mx-auto transition-all duration-700 ${
            ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl p-8 md:p-12 border border-gray-200 dark:border-gray-700 shadow-lg text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
                Learn More About Our Security
              </h2>
              <p className="text-lg text-textSecondary dark:text-[#858585] mb-8 max-w-2xl mx-auto leading-relaxed">
                Download our comprehensive security whitepaper to learn more about our security practices, compliance certifications, and data protection measures.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="primary"
                  size="lg"
                  className="bg-[#1E1E1E] dark:bg-[#1E1E1E] hover:bg-[#2D2D30] dark:hover:bg-[#2D2D30] text-white"
                  onClick={() => {
                    // TODO: Replace with actual whitepaper URL
                    window.open('#', '_blank');
                  }}
                >
                  <svg
                    className="w-5 h-5 mr-2 inline-block"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Download Security Whitepaper
                </Button>
                <Link to={ROUTES.CONTACT}>
                  <Button variant="outline" size="lg">
                    Contact Security Team
                  </Button>
                </Link>
              </div>
              <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-textTertiary dark:text-[#6A6A6A]">
                  Have specific security questions? Our security team is available to discuss your requirements.
                </p>
                <p className="text-sm text-textTertiary dark:text-[#6A6A6A] mt-2">
                  Email us at <a href="mailto:security@voxapilot.com" className="text-orange-600 dark:text-orange-500 hover:underline">security@voxapilot.com</a>
                </p>
              </div>
            </div>
          </div>
        </Container>
      </Section>
    </LandingLayout>
  );
}
